import React, { useEffect, useState } from "react";
import TuneSharp from "@mui/icons-material/TuneSharp";
import { CloseOutlined, Search } from "@mui/icons-material";
import { ALL_STATUSES, getStatusLabel } from "../../constants/journeyStatus";

const FilterDropdown = ({ object, setObject, handleFetch, isExporting }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState({});

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const initialFilters = {};
    object.forEach((item) => {
      initialFilters[item.searchFilter] = item.value || "";
    });
    setFilters(initialFilters);
  }, [object, isOpen]);

  const languages = [
    "English",
    "Hindi",
    "Marathi",
    "Gujarati",
    "Bengali",
    "Punjabi",
    "Kannada",
    "Tamil",
    "Telugu",
    "Malayalam",
    "Odia",
    "Assamese",
  ];

  const userRoles = [
    "USER","GCUSER",
    "SUPPORT",
    "ADMIN",
    "ASSESSOR"
  ];

  const statuses = ALL_STATUSES;

  // ---- verification score filter -------------------------------------------
  // The score filter needs two inputs (operator + threshold) but every filter
  // here carries a single value, and chips / URL sync / reset all depend on
  // that. So it is encoded as one string — ">=80", or "unscored" — and split
  // apart only for editing.
  const SCORE_OPERATORS = [">", "<", ">=", "<=", "=", "!="];
  const UNSCORED = "unscored";

  const splitScoreValue = (raw) => {
    if (!raw) return { operator: ">=", threshold: "" };
    if (String(raw).toLowerCase() === UNSCORED) {
      return { operator: UNSCORED, threshold: "" };
    }
    // Longest first, so ">=80" is not read as ">" followed by "=80".
    const match = [">=", "<=", "!=", ">", "<", "="].find((op) =>
      String(raw).startsWith(op)
    );
    return match
      ? { operator: match, threshold: String(raw).slice(match.length) }
      : { operator: "=", threshold: String(raw) };
  };

  const joinScoreValue = (operator, threshold) => {
    if (operator === UNSCORED) return UNSCORED;
    // An operator with no threshold is an unfinished filter — leave it unset
    // rather than sending ">=" and having the server ignore it silently.
    if (threshold === "" || threshold === null || threshold === undefined) {
      return "";
    }
    return `${operator}${threshold}`;
  };

  const handleApply = () => {
    const updatedObject = object.map((item) => {
      return { ...item, value: filters[item.searchFilter] || null };
    });
    setObject(updatedObject);
    toggleDropdown();
  };

  const handleReset = () => {
    const updatedFilters = object.map((item) => {
      return { ...item, value: null };
    });
    setFilters(updatedFilters);

    const updatedObject = object.map((item) => {
      return { ...item, value: null };
    });

    setObject(updatedObject);
    toggleDropdown();
  };

  return (
    <div className="flex  relative">
      {/* Filter Button */}
      <div className="flex flex-row">

        {object.map((item, index) => {
          if (item.value != null) {
            return (
              <button
                key={index}
                className="border mr-2 bg-[#38569B] text-white cursor-auto flex items-center border-[#E0E0E0] rounded py-1.5 px-3"
              >
                {item.label == "withTextMatching"
                  ? "Text Matching more than 40%"
                  : item.label === "Verification Score"
                    ? formatScoreChip(item.value)
                    : item.label === "Status"
                      ? getStatusLabel(item.value)
                      : item.value}
                <button
                  className="m-0 flex items-center"
                  onClick={() => {
                    setObject((prev) =>
                      prev.map((obj, i) =>
                        i === index ? { ...obj, value: null } : obj
                      )
                    );
                  }}
                >
                  <CloseOutlined
                    fontSize="small"
                    className=" cursor-pointer w-1 h-1 ml-1 border-gray-400 text-[#81A2EE]"
                  />
                </button>
              </button>
            );
          }
          return null;
        })}
        {handleFetch != null && object.some(item => item.value != null) && (
          <button
            className="border mr-2 bg-[#38569B] cursor-pointer text-white flex items-center border-[#E0E0E0] rounded py-1.5 px-3"
            onClick={() => handleFetch(true)}
            disabled={isExporting}
          >

            {isExporting ? "Exporting Journeys..." : "Export Journeys"}
          </button>
        )}

        <button
          className="border focus:border-blue-600 flex items-center border-[#E0E0E0] rounded py-1.5 px-3"
          onClick={toggleDropdown}
        >
          <TuneSharp fontSize="small" className="mr-1" />
          Filter
        </button>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full right-0 z-10 w-96 bg-white rounded-lg shadow">
          <div className="bg-gray-100 px-4 py-3 border-b border-[#E0E0E0] flex items-center mb-4">
            <p className="text-base font-bold text-gray-900">Filter</p>
            <button
              onClick={() => {
                toggleDropdown();
              }}
              className="absolute top-3 right-2"
            >
              <CloseOutlined className="w-5 h-5 border-gray-400" />
            </button>
          </div>

          {/* Date Range Filter */}
          <div className="">
            <div className="mb-4 px-4">
              <div className="flex w-full flex-row justify-between">
                <p className="text-sm font-medium text-[#a09faa] mb-2">
                  Select Date
                </p>
                <p
                  className="text-sm font-semibold text-blue-600 cursor-pointer"
                  onClick={() => setFilters({ ...filters, start: "", end: "" })}
                >
                  Clear
                </p>
              </div>
              <div className="flex w-full flex-row ">
                <div className="flex flex-col w-full mr-1">
                  <p className="text-sm mb-1">From:</p>
                  <input
                    type="date"
                    value={filters.start}
                    max={filters.end || null}
                    onChange={(e) =>
                      setFilters({ ...filters, start: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded p-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div className="flex flex-col w-full ml-1">
                  <p className="text-sm mb-1">To:</p>
                  <input
                    type="date"
                    min={filters.start || null}
                    value={filters.end}
                    disabled={!filters.start}
                    onChange={(e) =>
                      setFilters({ ...filters, end: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded p-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>

            {/* Keyword Search */}
            {object.map((item, index) =>
              item.label ? (
                item.label === "Language" ? (
                  <div className="mb-4 px-4" key={index}>
                    <div className="flex w-full flex-row justify-between">
                      <p className="text-sm font-medium text-[#a09faa] mb-2">
                        Search by {item.label}
                      </p>
                      <p
                        className="text-sm font-semibold text-blue-600 cursor-pointer"
                        onClick={() =>
                          setFilters({ ...filters, [item.searchFilter]: "" })
                        }
                      >
                        Clear
                      </p>
                    </div>

                    <div className="flex border w-full border-gray-300 rounded p-2 flex-row">
                      <Search fontSize="small" />
                      <select
                        value={
                          filters[item.searchFilter]
                            ? filters[item.searchFilter]
                            : "Select Language"
                        }
                        onChange={(e) => {
                          setFilters({
                            ...filters,
                            [item.searchFilter]: e.target.value,
                          });
                        }}
                        className="w-full ml-1 text-sm focus:outline-none"
                      >
                        <option disabled value="Select Language">
                          Search by Language
                        </option>
                        {languages.map((language, index) => (
                          <option key={index} value={language}>
                            {language}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : item.label === "Product Type" ? (
                  <div className="mb-4 px-4" key={index}>
                    <div className="flex w-full flex-row justify-between">
                      <p className="text-sm font-medium text-[#a09faa] mb-2">
                        Search by {item.label}
                      </p>
                      <p
                        className="text-sm font-semibold text-blue-600 cursor-pointer"
                        onClick={() =>
                          setFilters({ ...filters, [item.searchFilter]: "" })
                        }
                      >
                        Clear
                      </p>
                    </div>

                    <div className="flex border w-full border-gray-300 rounded p-2 flex-row">
                      <Search fontSize="small" />
                      <select
                        value={
                          filters[item.searchFilter]
                            ? filters[item.searchFilter]
                            : "Select Product Type"
                        }
                        onChange={(e) => {
                          setFilters({
                            ...filters,
                            [item.searchFilter]: e.target.value,
                          });
                        }}
                        className="w-full ml-1 text-sm focus:outline-none"
                      >
                        <option disabled value="Select Product Type">
                          Search by Product Type
                        </option>
                        {["SINGLE", "MULTIPLE"].map((ptype, index) => (
                          <option key={index} value={ptype}>
                            {ptype}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : item.label === "Status" || item.label === "Role" ? (
                  <div className="mb-4 px-4" key={index}>
                    <div className="flex w-full flex-row justify-between">
                      <p className="text-sm font-medium text-[#a09faa] mb-2">
                        Search by {item.label}
                      </p>
                      <p
                        className="text-sm font-semibold text-blue-600 cursor-pointer"
                        onClick={() =>
                          setFilters({ ...filters, [item.searchFilter]: "" })
                        }
                      >
                        Clear
                      </p>
                    </div>

                    <div className="flex border w-full border-gray-300 rounded p-2 flex-row">
                      <Search fontSize="small" />
                      <select
                        value={
                          filters[item.searchFilter] &&
                            filters[item.searchFilter] != ""
                            ? filters[item.searchFilter]
                            : item.label
                        }
                        onChange={(e) => {
                          setFilters({
                            ...filters,
                            [item.searchFilter]: e.target.value,
                          });
                        }}
                        className="w-full ml-1 text-sm focus:outline-none"
                      >
                        <option disabled value={item.label}>
                          Search by {item.label}
                        </option>
                        {item.label === "Status" ? statuses.map((status, index) => (
                          <option key={index} value={status}>
                            {getStatusLabel(status)}
                          </option>
                        )) : userRoles.map((role, index) => (
                          <option key={index} value={role}>
                            {role === "SUPPORT" ? "IDM SUPPORT" : role}

                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : item.label === "Verification Score" ? (
                  (() => {
                    const { operator, threshold } = splitScoreValue(
                      filters[item.searchFilter]
                    );
                    return (
                      <div className="mb-4 px-4" key={index}>
                        <div className="flex w-full flex-row justify-between">
                          <p className="text-sm font-medium text-[#a09faa] mb-2">
                            Search by {item.label}
                          </p>
                          <p
                            className="text-sm font-semibold text-blue-600 cursor-pointer"
                            onClick={() =>
                              setFilters({ ...filters, [item.searchFilter]: "" })
                            }
                          >
                            Clear
                          </p>
                        </div>

                        <div className="flex w-full flex-row gap-2">
                          <select
                            value={operator}
                            onChange={(e) =>
                              setFilters({
                                ...filters,
                                [item.searchFilter]: joinScoreValue(
                                  e.target.value,
                                  threshold
                                ),
                              })
                            }
                            className="border border-gray-300 rounded p-2 text-sm focus:outline-none w-28"
                          >
                            {SCORE_OPERATORS.map((op) => (
                              <option key={op} value={op}>
                                {op}
                              </option>
                            ))}
                            <option value={UNSCORED}>Unscored</option>
                          </select>

                          {/* Unscored is "no score exists", so a threshold
                              would be meaningless next to it. */}
                          {operator !== UNSCORED && (
                            <input
                              type="number"
                              min={0}
                              max={100}
                              step={0.1}
                              placeholder="0 - 100"
                              value={threshold}
                              onChange={(e) =>
                                setFilters({
                                  ...filters,
                                  [item.searchFilter]: joinScoreValue(
                                    operator,
                                    e.target.value
                                  ),
                                })
                              }
                              className="flex-1 border border-gray-300 rounded p-2 text-sm focus:outline-none"
                            />
                          )}
                        </div>
                      </div>
                    );
                  })()
                ) : item.label === "withTextMatching" ? (
                  <div className="mb-4 px-4">
                    <div className="flex w-full justify-between items-center">
                      <p className="text-sm font-medium text-[#a09faa]">Show journeys with text matching more than 40%</p>
                      <input
                        type="checkbox"
                        checked={filters.withTextMatching || false}
                        onChange={(e) =>
                          setFilters({ ...filters, withTextMatching: e.target.checked })
                        }
                        className="w-4 h-4 accent-blue-600 cursor-pointer"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="mb-4 px-4" key={index}>
                    <div className="flex w-full flex-row justify-between">
                      <p className="text-sm font-medium text-[#a09faa] mb-2">
                        Search by {item.label}
                      </p>
                      <p
                        className="text-sm font-semibold text-blue-600 cursor-pointer"
                        onClick={() =>
                          setFilters({ ...filters, [item.searchFilter]: "" })
                        }
                      >
                        Clear
                      </p>
                    </div>

                    <div className="flex border w-full border-gray-300 rounded p-2 flex-row">
                      <Search fontSize="small" />
                      <input
                        type="text"
                        placeholder={`Search by ${item.label}`}
                        value={filters[item.searchFilter] || ""}
                        onChange={(e) =>
                          setFilters({
                            ...filters,
                            [item.searchFilter]: e.target.value,
                          })
                        }
                        className="w-full ml-1 text-sm focus:outline-none"
                      />
                    </div>
                  </div>
                )
              ) : null
            )}

            <hr />

            {/* Buttons */}
            <div className="flex justify-between px-4 py-3">
              <button
                className="text-sm font-medium px-2 py-2 border border-[#E0E0E0] rounded"
                onClick={() => {
                  handleReset();
                }}
              >
                Reset
              </button>
              <button
                className="bg-blue-600 text-white text-sm font-medium py-2 px-4 rounded"
                onClick={handleApply}
              >
                Apply Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Human-readable label for the active-filter chip. The stored value is the wire
 * format (">=80"), which reads as noise on a chip.
 */
function formatScoreChip(value) {
  if (!value) return "";
  if (String(value).toLowerCase() === "unscored") return "Unscored";
  return `Score ${String(value).replace(/^(>=|<=|!=|>|<|=)/, "$1 ")}`;
}

export default FilterDropdown;
